import React, { useEffect, useMemo, useState } from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import {
  TextInput as PaperTextInput,
  Button as PaperButton,
  Checkbox,
  HelperText,
} from 'react-native-paper';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Feather from '@react-native-vector-icons/feather/static';
import { GTFonts, useTheme } from '../../theme';
import { borderWidth, fs, s, v } from '../../utils/libs';
import KeyboardAwareFormScroll from '../../components/Reusables/KeyboardAwareFormScroll';
import {
  createGoodsReceipt,
  getPurchaseOrderForReceipt,
  getPurchaseOrdersInformation,
  getSuppliersInformation,
  parseCreatedGoodsReceiptResponse,
  parsePurchaseOrderResponse,
  parsePurchaseOrdersResponse,
  parseSuppliersResponse,
} from '../../api/backofficeSuppliers';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import {
  setGoodsReceiptsByPOIdData,
  setPurchaseOrderByIdData,
  setPurchaseOrdersBySupplierIdData,
  setPurchaseOrdersBySupplierIdError,
  setPurchaseOrdersBySupplierIdLoading,
  setSuppliersListData,
  upsertPurchaseOrderBySupplierId,
} from '../../redux/backoffice/suppliersSlice';
import { AppSnackbar } from '../../services/AppSnackbar';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import {
  formatQuantityWithUnit,
  normalizePurchaseOrderLinesForReceipt,
  parseNumberLike,
} from '../../utils/backofficeSuppliers';
import {
  canCreateGoodsReceipt,
  getGoodsReceiptBlockedMessage,
  toGoodsReceiptPurchaseOrderOption,
} from '../../utils/purchaseOrderStatus';
import {
  parseFormattedNumericString,
  sanitizeNumericInput,
} from '../../utils/libs';
import { getApiMessage } from '../../utils/apiError';
import FormSelectField from '../../components/Reusables/FormSelectField';
import AppSkeleton from '../../components/Reusables/AppSkeleton';
import SupplierPrerequisiteEmptyState from '../../components/suppliers/SupplierPrerequisiteEmptyState';
import type { RootStackParamList } from '../../navigation/AppStack';

type RouteParams = {
  purchaseOrderId?: string;
  supplierId?: string;
};

type ReceiptLineInput = {
  poLineId: string;
  variantId?: string;
  variantDisplayName?: string;
  productName: string;
  productDisplayName: string;
  quantityDisplay: string;
  ordered: number;
  received: number;
  remaining: number;
  isUnit: boolean;
  uom: string;
  quantityReceived: string;
};

const BackOfficeCreateGoodsReceiptScreen: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const route = useRoute();
  const { purchaseOrderId: routePurchaseOrderId, supplierId: routeSupplierId } =
    (route.params || {}) as RouteParams;
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const dispatch = useAppDispatch();

  const [supplierIdSel, setSupplierIdSel] = useState(
    String(routeSupplierId || ''),
  );
  const [purchaseOrderIdSel, setPurchaseOrderIdSel] = useState(
    String(routePurchaseOrderId || ''),
  );
  const [lineInputs, setLineInputs] = useState<ReceiptLineInput[]>([]);
  const [notes, setNotes] = useState('');
  const [createExpense, setCreateExpense] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadedPOStatus, setLoadedPOStatus] = useState<string | null>(null);

  const suppliers = useAppSelector(
    state => state.backofficeSuppliers.list.items,
  );
  const purchaseOrdersCache = useAppSelector(
    state =>
      state.backofficeSuppliers.purchaseOrdersBySupplierId[
        String(supplierIdSel || '')
      ],
  );
  const purchaseOrders = Array.isArray(purchaseOrdersCache?.data)
    ? purchaseOrdersCache.data
    : [];

  const effectivePurchaseOrderId =
    routePurchaseOrderId || purchaseOrderIdSel || '';

  const goodsReceiptsByPOEntity = useAppSelector(
    state =>
      state.backofficeSuppliers.goodsReceiptsByPOId[
        String(effectivePurchaseOrderId || '')
      ],
  );

  const supplierOptions = useMemo(
    () =>
      suppliers.map((supplier: any) => ({
        label: String(supplier?.bp_supplier_name || 'Unnamed supplier'),
        value: String(supplier?._id),
      })),
    [suppliers],
  );

  const purchaseOrderOptions = useMemo(
    () => purchaseOrders.map(toGoodsReceiptPurchaseOrderOption),
    [purchaseOrders],
  );

  const purchaseOrderPlaceholder = useMemo(() => {
    if (!supplierIdSel) return 'Select supplier first';
    return 'Select purchase order';
  }, [supplierIdSel]);

  const isStandaloneMode = !routePurchaseOrderId;

  const selectedPO = useMemo(
    () =>
      purchaseOrders.find(
        (po: any) => String(po?._id) === purchaseOrderIdSel,
      ) ?? null,
    [purchaseOrders, purchaseOrderIdSel],
  );
  const receiptPO = useMemo(() => {
    if (isStandaloneMode) return selectedPO;
    if (!loadedPOStatus) return null;
    return {
      _id: effectivePurchaseOrderId,
      bp_purchase_order_status: loadedPOStatus,
    };
  }, [effectivePurchaseOrderId, isStandaloneMode, loadedPOStatus, selectedPO]);
  const isSelectedPOBlocked =
    receiptPO != null && !canCreateGoodsReceipt(receiptPO);
  const blockedHelperMessage = isSelectedPOBlocked
    ? getGoodsReceiptBlockedMessage(
        isStandaloneMode
          ? selectedPO?.bp_purchase_order_status
          : loadedPOStatus,
      )
    : null;

  useEffect(() => {
    setSupplierIdSel(String(routeSupplierId || ''));
    setPurchaseOrderIdSel(String(routePurchaseOrderId || ''));
  }, [routeSupplierId, routePurchaseOrderId]);

  useEffect(() => {
    if (!owner?.accessToken || !isStandaloneMode) return;
    setLoadingSuppliers(true);
    getSuppliersInformation(axiosBackoffice as any, 1, 100, 'active', {
      accessToken: owner.accessToken,
    })
      .then(res => {
        setLoadingSuppliers(false);
        const parsed = parseSuppliersResponse(res);
        if (parsed?.success && parsed.suppliers) {
          dispatch(
            setSuppliersListData({
              suppliers: parsed.suppliers,
              total: parsed.total ?? parsed.suppliers.length,
              page: parsed.page ?? 1,
              limit: parsed.limit ?? 100,
            }),
          );
        }
      })
      .catch((error: any) => {
        setLoadingSuppliers(false);
        AppSnackbar.show(
          getApiMessage(error, 'Failed to load suppliers'),
          'error',
        );
      });
  }, [axiosBackoffice, dispatch, isStandaloneMode, owner?.accessToken]);

  useEffect(() => {
    if (!supplierIdSel || !owner?.accessToken || !isStandaloneMode) return;

    let mounted = true;
    dispatch(setPurchaseOrdersBySupplierIdLoading(supplierIdSel));
    getPurchaseOrdersInformation(
      axiosBackoffice as any,
      { supplierId: supplierIdSel },
      { accessToken: owner.accessToken },
    )
      .then(res => {
        if (!mounted) return;
        const parsed = parsePurchaseOrdersResponse(res);
        if (parsed?.success) {
          dispatch(
            setPurchaseOrdersBySupplierIdData({
              id: supplierIdSel,
              data: parsed.purchaseOrders || [],
            }),
          );
        } else {
          dispatch(
            setPurchaseOrdersBySupplierIdError({
              id: supplierIdSel,
              error: parsed?.message || 'Failed to load purchase orders',
            }),
          );
        }
      })
      .catch((error: any) => {
        if (!mounted) return;
        dispatch(
          setPurchaseOrdersBySupplierIdError({
            id: supplierIdSel,
            error: getApiMessage(error, 'Failed to load purchase orders'),
          }),
        );
      });

    return () => {
      mounted = false;
    };
  }, [
    axiosBackoffice,
    dispatch,
    isStandaloneMode,
    owner?.accessToken,
    supplierIdSel,
  ]);

  useEffect(() => {
    if (!effectivePurchaseOrderId || !owner?.accessToken) return;

    if (isStandaloneMode && selectedPO && !canCreateGoodsReceipt(selectedPO)) {
      setLoading(false);
      setLoadError(null);
      setLoadedPOStatus(selectedPO.bp_purchase_order_status ?? null);
      setLineInputs([]);
      return;
    }

    let mounted = true;
    setLoading(true);
    setLoadError(null);
    setLoadedPOStatus(null);

    getPurchaseOrderForReceipt(
      axiosBackoffice as any,
      effectivePurchaseOrderId,
      {
        accessToken: owner.accessToken,
      },
    )
      .then(res => {
        console.log('getPurchaseOrderForReceipt', res);
        if (!mounted) return;
        const parsed = parsePurchaseOrderResponse(res);
        if (!parsed?.success || !parsed.purchaseOrder) {
          setLoadError(parsed?.message || 'Failed to load purchase order');
          setLineInputs([]);
          return;
        }

        setLoadedPOStatus(
          parsed.purchaseOrder?.bp_purchase_order_status ?? null,
        );

        if (!canCreateGoodsReceipt(parsed.purchaseOrder)) {
          setLineInputs([]);
          return;
        }

        const normalizedLines = normalizePurchaseOrderLinesForReceipt(
          parsed.purchaseOrder,
        );
        const inputs = normalizedLines
          .filter((line: any) => Boolean(line?.lineId))
          .map((line: any) => ({
            poLineId: String(line.lineId),
            variantId: String(line.variantId || ''),
            variantDisplayName: String(line.variantDisplayName || ''),
            productName: String(line.productName || ''),
            productDisplayName: String(line.productDisplayName || ''),
            quantityDisplay: String(line.quantityDisplay || ''),
            ordered: parseNumberLike(line.ordered),
            received: parseNumberLike(line.received),
            remaining: parseNumberLike(line.remaining),
            isUnit: Boolean(line.isUnit),
            uom: String(line.uom || ''),
            quantityReceived: '',
          }));
        setLineInputs(inputs);
      })
      .catch((error: any) => {
        if (!mounted) return;
        setLoadError(getApiMessage(error, 'Failed to load purchase order'));
        setLineInputs([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [
    axiosBackoffice,
    effectivePurchaseOrderId,
    isStandaloneMode,
    owner?.accessToken,
    selectedPO,
  ]);

  const canSubmit = useMemo(() => {
    if (
      !effectivePurchaseOrderId ||
      loading ||
      saving ||
      loadError ||
      isSelectedPOBlocked
    )
      return false;

    const hasAnyPositive = lineInputs.some(line => {
      const raw = String(line.quantityReceived || '').trim();
      if (!raw) return false;
      const quantity = Number(parseFormattedNumericString(raw) || 0);
      return quantity > 0;
    });

    const hasAnyInvalid = lineInputs.some(line => {
      const raw = String(line.quantityReceived || '').trim();
      if (!raw) return false;
      const quantity = Number(parseFormattedNumericString(raw) || 0);
      return quantity < 0 || quantity > line.remaining;
    });

    return hasAnyPositive && !hasAnyInvalid;
  }, [
    effectivePurchaseOrderId,
    isSelectedPOBlocked,
    lineInputs,
    loadError,
    loading,
    saving,
  ]);

  const handleSubmit = async () => {
    if (!canSubmit || !owner?.accessToken || !effectivePurchaseOrderId) return;

    const hasAnyInvalid = lineInputs.some(line => {
      const raw = String(line.quantityReceived || '').trim();
      if (!raw) return false;
      const quantity = Number(parseFormattedNumericString(raw) || 0);
      return quantity < 0 || quantity > line.remaining;
    });
    if (hasAnyInvalid) return;

    const lineItems = lineInputs
      .filter(line => {
        const quantity = Number(
          parseFormattedNumericString(line.quantityReceived) || 0,
        );
        return (
          quantity > 0 && quantity <= line.remaining && Boolean(line.poLineId)
        );
      })
      .map(line => {
        const raw = Number(
          parseFormattedNumericString(line.quantityReceived) || 0,
        );
        return {
          poLineId: line.poLineId,
          bp_gr_line_variant_id: line.variantId || undefined,
          quantityReceived: line.isUnit ? raw : Math.floor(raw),
        };
      });

    setSaving(true);
    try {
      const res = await createGoodsReceipt(
        axiosBackoffice as any,
        {
          purchaseOrderId: effectivePurchaseOrderId,
          lineItems,
          notes: notes.trim() || undefined,
          createExpense,
        },
        { accessToken: owner.accessToken },
      );
      const ok = res?.data?.success === true || res?.data?.status === 200;
      AppSnackbar.show(
        String(
          res?.data?.message ||
            (ok
              ? 'Goods receipt created successfully'
              : 'Failed to create goods receipt'),
        ),
        ok ? 'success' : 'error',
      );
      if (!ok) return;
      appAnalytics.track('procurement_gr_created', {
        ...buildAppAnalyticsContext(),
        line_count: lineItems.length,
        create_expense: createExpense,
      });
      const parsedCreate = parseCreatedGoodsReceiptResponse(res);
      const createdGoodsReceipt = parsedCreate?.goodsReceipt;
      const createdPO = parsedCreate?.purchaseOrder;
      const resolvedPOId = String(effectivePurchaseOrderId || '');

      if (createdGoodsReceipt && resolvedPOId) {
        const currentGoodsReceipts = Array.isArray(
          goodsReceiptsByPOEntity?.data,
        )
          ? goodsReceiptsByPOEntity.data
          : [];
        const createdGoodsReceiptId = String(createdGoodsReceipt?._id || '');
        const mergedGoodsReceipts = createdGoodsReceiptId
          ? [
              createdGoodsReceipt,
              ...currentGoodsReceipts.filter(
                (item: any) =>
                  String(item?._id || '') !== createdGoodsReceiptId,
              ),
            ]
          : [createdGoodsReceipt, ...currentGoodsReceipts];
        dispatch(
          setGoodsReceiptsByPOIdData({
            id: resolvedPOId,
            data: mergedGoodsReceipts,
          }),
        );
      }

      if (createdPO && typeof createdPO === 'object') {
        const resolvedPOEntityId = String(createdPO?._id || resolvedPOId);
        dispatch(
          setPurchaseOrderByIdData({
            id: resolvedPOEntityId,
            data: createdPO,
          }),
        );
        const resolvedSupplierId = String(
          (typeof createdPO.bp_purchase_order_supplier_id === 'object'
            ? createdPO.bp_purchase_order_supplier_id?._id
            : createdPO.bp_purchase_order_supplier_id) ||
            routeSupplierId ||
            supplierIdSel ||
            '',
        );
        if (resolvedSupplierId) {
          dispatch(
            upsertPurchaseOrderBySupplierId({
              supplierId: resolvedSupplierId,
              order: createdPO,
            }),
          );
        }
      }

      navigation.replace('BackOfficePurchaseOrderDetails', {
        supplierId: String(routeSupplierId || supplierIdSel || ''),
        purchaseOrderId: String(effectivePurchaseOrderId),
      });
    } catch (error: any) {
      AppSnackbar.show(
        getApiMessage(error, 'Failed to create goods receipt'),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.container}>
      <KeyboardAwareFormScroll
        style={styles.scroll}
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        // baseBottomPadding={v(80)}
        bottomOffset={v(240)}>
        <Text style={styles.subtitle}>
          Record goods received against a purchase order and update stock.
        </Text>

        <Text style={styles.sectionTitle}>Goods Receipt Details</Text>
        {isStandaloneMode && (
          <>
            {!loadingSuppliers &&
            supplierOptions.length === 0 &&
            !supplierIdSel ? (
              <SupplierPrerequisiteEmptyState
                description="Add a supplier before receiving goods."
                onAddSupplier={() =>
                  navigation.navigate('BackOfficeAddSupplier')
                }
              />
            ) : (
              <FormSelectField
                // label="Supplier *"
                value={supplierIdSel}
                onValueChange={value => {
                  setSupplierIdSel(String(value || ''));
                  setPurchaseOrderIdSel('');
                  setLineInputs([]);
                  setLoadError(null);
                }}
                options={supplierOptions}
                placeholder="Select supplier"
                loading={loadingSuppliers}
              />
            )}

            <FormSelectField
              // label="Purchase Order *"
              value={purchaseOrderIdSel}
              onValueChange={value => {
                setPurchaseOrderIdSel(String(value || ''));
                setLineInputs([]);
                setLoadError(null);
                setLoadedPOStatus(null);
              }}
              options={purchaseOrderOptions}
              disabled={!supplierIdSel}
              loading={!!purchaseOrdersCache?.loading}
              placeholder={purchaseOrderPlaceholder}
            />
            {blockedHelperMessage ? (
              <HelperText type="error" visible>
                {blockedHelperMessage}
              </HelperText>
            ) : null}
          </>
        )}

        {!isStandaloneMode && blockedHelperMessage ? (
          <HelperText type="error" visible>
            {blockedHelperMessage}
          </HelperText>
        ) : null}

        <Text style={styles.sectionTitle}>Items</Text>
        {!effectivePurchaseOrderId && isStandaloneMode ? (
          <Text style={styles.helpText}>
            Select a supplier and purchase order to continue.
          </Text>
        ) : loading ? (
          <AppSkeleton mode="receipt_items" count={3} />
        ) : loadError ? (
          <Text style={styles.errorText}>{loadError}</Text>
        ) : lineInputs.length === 0 ? (
          <Text style={styles.helpText}>
            No line items available to receive.
          </Text>
        ) : (
          lineInputs.map((line, index) => {
            const displayName =
              line.productDisplayName ||
              line.productName ||
              `Line ${index + 1}`;
            const orderedDisplay = line.quantityDisplay
              ? line.quantityDisplay
              : formatQuantityWithUnit(line.ordered, {
                  isUnit: line.isUnit,
                  uom: line.uom,
                });
            const receivedDisplay = formatQuantityWithUnit(line.received, {
              isUnit: line.isUnit,
              uom: line.uom,
            });
            const remainingDisplay = formatQuantityWithUnit(line.remaining, {
              isUnit: line.isUnit,
              uom: line.uom,
            });

            return (
              <View style={styles.lineCard} key={line.poLineId}>
                <Text style={styles.lineTitle}>{displayName}</Text>
                {line.variantDisplayName ? (
                  <Text style={styles.metaText}>
                    Variant: {line.variantDisplayName}
                  </Text>
                ) : null}
                <Text style={styles.metaText}>Ordered: {orderedDisplay}</Text>
                <Text style={styles.metaText}>Received: {receivedDisplay}</Text>
                <Text style={styles.metaText}>
                  Remaining: {remainingDisplay}
                </Text>
                {line.remaining > 0 ? (
                  <PaperTextInput
                    mode="outlined"
                    label={`Quantity Received${line.isUnit && line.uom ? ` (${line.uom})` : ''}`}
                    placeholder={
                      line.isUnit && line.uom ? `0 ${line.uom}` : '0'
                    }
                    keyboardType="numeric"
                    value={line.quantityReceived}
                    onChangeText={text => {
                      const cleaned = sanitizeNumericInput(text, {
                        isUnit: line.isUnit,
                      });
                      setLineInputs(prev =>
                        prev.map((item, idx) =>
                          idx === index
                            ? {
                                ...item,
                                quantityReceived: cleaned,
                              }
                            : item,
                        ),
                      );
                    }}
                    right={
                      line.isUnit && line.uom ? (
                        <PaperTextInput.Affix
                          textStyle={styles.uomAffix}
                          text={` ${line.uom}`}
                        />
                      ) : undefined
                    }
                    style={styles.input}
                  />
                ) : (
                  <Text style={styles.helpText}>Fully received</Text>
                )}
              </View>
            );
          })
        )}

        <Text style={styles.sectionTitle}>Notes</Text>
        <PaperTextInput
          mode="outlined"
          label="i.e. Damaged carton on arrival."
          multiline
          value={notes}
          onChangeText={setNotes}
          placeholder="i.e. Damaged carton on arrival. "
          style={[styles.input, { height: v(100) }]}
        />

        <TouchableOpacity
          style={styles.checkboxRow}
          onPress={() => setCreateExpense(prev => !prev)}
          activeOpacity={0.7}>
          {Platform.OS === 'web' ? (
            <Feather
              name={createExpense ? 'check-square' : 'square'}
              size={fs(20)}
              color={
                createExpense
                  ? theme.colors.primary
                  : theme.colors.textSecondary
              }
            />
          ) : (
            <Checkbox.Android
              status={createExpense ? 'checked' : 'unchecked'}
              onPress={() => setCreateExpense(prev => !prev)}
              color={theme.colors.primary}
            />
          )}
          <Text style={styles.checkboxLabel}>
            Record as expense
          </Text>
        </TouchableOpacity>
        <Text style={styles.checkboxHelperText}>
          {/* Check this if you won't be creating a separate supplier invoice
          for this delivery.  */}
          Create a stock expense record for received goods so your business
          expenses stay up to date.
        </Text>
      </KeyboardAwareFormScroll>

      <KeyboardStickyView offset={{ closed: 0, opened: v(30) }}>
        <View style={styles.stickyButtonContainer}>
          <View style={styles.actions}>
            <PaperButton
              mode="outlined"
              onPress={() => navigation.goBack()}
              disabled={saving}
              style={styles.actionButton}
              contentStyle={[styles.actionButtonContent, { height: v(50) }]}
              labelStyle={styles.actionLabel}
              textColor={theme.colors.textSecondary}
              theme={{ colors: { outline: theme.colors.border } }}
              uppercase={false}>
              Cancel
            </PaperButton>
            <PaperButton
              mode="contained"
              onPress={handleSubmit}
              disabled={!canSubmit}
              loading={saving}
              style={styles.actionButton}
              labelStyle={styles.actionLabel}
              contentStyle={[styles.actionButtonContent, { height: v(50) }]}
              buttonColor={theme.colors.successDark}
              textColor={theme.colors.textInverse}
              uppercase={false}>
              {saving ? 'Creating...' : 'Create Goods Receipt'}
            </PaperButton>
          </View>
        </View>
      </KeyboardStickyView>
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    scroll: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
      }),
    },
    content: {
      padding: s(12),
      gap: v(10),
      paddingBottom: v(24),
      ...(Platform.OS === 'web' && {
        padding: s(24),
        gap: v(20),
      }),
    },
    sectionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.textDark,
      marginVertical: v(6),
      ...(Platform.OS === 'web' && {
        fontSize: fs(18),
        marginBottom: v(12),
        paddingBottom: v(8),
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.borderGray,
      }),
    },
    subtitle: {
      marginBottom: v(8),
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(17),
      color: theme.colors.textSecondary,
    },
    lineCard: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: theme.borderRadius.md,
      padding: s(10),
      gap: v(6),
      backgroundColor: theme.colors.surface,
      shadowOpacity: 0,
      shadowRadius: 0,
      elevation: 0,
    },
    lineTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    metaText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    helpText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    errorText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.error,
    },
    input: {
      backgroundColor: theme.colors.surface,
      height: v(50),
      borderRadius: theme.borderRadius.md,
      ...(Platform.OS === 'web' && {
        height: v(56),
      }),
    },
    uomAffix: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    checkboxRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: v(12),
    },
    checkboxLabel: {
      fontFamily: GTFonts.bold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
      marginLeft: Platform.OS === 'web' ? s(8) : s(4),
    },
    checkboxHelperText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      marginLeft: Platform.OS === 'web' ? s(28) : s(40),
      marginTop: v(-4),
    },
    stickyButtonContainer: {
      backgroundColor: theme.colors.surface,
      paddingHorizontal: s(8),
      paddingVertical: s(12),
      borderTopWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      borderTopColor: theme.colors.border,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        paddingHorizontal: s(24),
        borderTopWidth: 0,
      }),
    },
    actions: {
      flexDirection: 'row',
      gap: s(8),
    },
    actionButton: {
      flex: 1,
      borderRadius: theme.borderRadius.md,
    },
    actionButtonContent: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    actionLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      textAlign: 'center',
    },
  });

export default BackOfficeCreateGoodsReceiptScreen;
